import { apiPath } from ".";

/**
 * Result of login request
 */
export interface LoginResult {
  /** When "ok", check special case {ILoginResultSuccess} */
  status: "ok" | "wrong-password" | "inactive-account" | "server-error";
}

/**
 * Successful login request, containing the user's full name and a session ID
 */
export interface LoginResultSuccess extends LoginResult {
  status: "ok";
  sessionId: string;
  fullName: string;
  isAdmin: boolean;
  userId: number;
}

/**
 * Request a sessionID with the given email/password
 *
 * @see ILoginResult
 * @see ILoginResultSuccess, for successful requests
 */
export async function login(
  email: string,
  password: string
): Promise<LoginResult | LoginResultSuccess> {
  const request: RequestInit = {
    method: "POST",
    mode: "cors",
    cache: "no-cache",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json; charset=utf-8",
    },
    redirect: "follow",
    body: JSON.stringify({ email, password }),
  };

  let response;
  try {
    response = await fetch(`${apiPath}/auth/login`, request);
  } catch (err) {
    return { status: "server-error" };
  }

  if (response.status === 401) {
    return { status: "wrong-password" };
  }

  return response.json();
}

/**
 * Registers a new account
 * @param email     The user's email address
 * @param password  The user's password, in plaintest
 * @param fullName  The user's full name (not really validated)
 */
export async function register(
  email: string,
  password: string,
  fullName: string
): Promise<
  "success" | "account-already-exists" | "failure" | "connection-error"
> {
  const request: RequestInit = {
    method: "POST",
    mode: "cors",
    cache: "no-cache",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json; charset=utf-8",
    },
    redirect: "follow",
    body: JSON.stringify({ email, password, fullName }),
  };

  try {
    const response = await fetch(`${apiPath}/auth/register`, request);

    switch (response.status) {
      case 202:
        return "success";
      case 409:
        return "account-already-exists";
      default:
        return "failure";
    }
  } catch (err) {
    return "connection-error";
  }
}

/**
 * Verify a sessionId to check if it's still valid for use
 */
export async function verifyCookie(): Promise<boolean> {
  const request: RequestInit = {
    method: "GET",
    mode: "cors",
    cache: "no-cache",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json; charset=utf-8",
    },
    redirect: "follow",
  };

  const response = await fetch(`${apiPath}/auth/verify`, request);
  return response.ok;
}

export async function logout(): Promise<boolean> {
  const request: RequestInit = {
    method: "GET",
    mode: "cors",
    cache: "no-cache",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json; charset=utf-8",
    },
    redirect: "follow",
  };

  try {
    await fetch(`${apiPath}/auth/logout`, request);
  } catch (err) {
    return false;
  }

  return true;
}

/**
 * Calls the appropriate API endpoint to change the user's password.
 * @param email The user's email address
 * @param oldPassword The current account password
 * @param newPassword The new desired password
 * @returns The request's result
 */
export async function passwd(
  email: string,
  oldPassword: string,
  newPassword: string
): Promise<"ok" | "bad-password" | "error" | "network-error"> {
  const request: RequestInit = {
    method: "POST",
    mode: "cors",
    cache: "no-cache",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json; charset=utf-8",
    },
    redirect: "follow",
    body: JSON.stringify({ email, password: oldPassword, newPassword }),
  };

  try {
    const response = await fetch(`${apiPath}/auth/passwd`, request);

    switch (response.status) {
      case 200:
        return "ok";
      case 401:
        return "bad-password";
      default:
        return "error";
    }
  } catch (err) {
    return "network-error";
  }
}
